import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import { assignFormStatus, formConfigurationQuery, type FormConfigurationType } from '../../api/settingsEaseedeskFormConfiguration'
import { LoadFailed } from '../../ui/LoadFailed'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Tickets -> Ticket form configuration, element cu
 * element fata de `easeedesk_settings/tickets/ticket_form_configuration/
 * index.php`: filtrul "Show" pe tip (in pagina, nu pe server - randurile
 * sunt deja aici), un rand pe tip cu tabelul statusurilor fluxului si
 * selectorul de formular pe fiecare; schimbat -> se salveaza pe loc
 * ("Updates saved."), ca in `index-1.js`.
 */
export function TicketFormConfigurationPage() {
  const client = useQueryClient()
  const page = useQuery(formConfigurationQuery())
  const [filter, setFilter] = useState('')

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="Ticket form configuration" />
  if (!page.data) return <Spinner />

  const onAssign = (type: FormConfigurationType, statusId: number, formId: string) => {
    void assignFormStatus(type.ID, statusId, formId)
      .then(() => {
        toast('Updates saved.', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_form_configuration'] })
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }

  const formOptions = [
    { value: '0', label: 'Select form...' },
    ...page.data.form_list.map((f) => ({ value: String(f.ID), label: f.form_name })),
  ]
  const types = page.data.type_entities

  return (
    <div>
      <h4>
        <strong>Ticket</strong> form configuration
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit ticket <strong>forms</strong>
      </label>
      <hr />

      <div className="form-inline" style={{ marginBottom: 14 }}>
        <label htmlFor="form-config-type-filter" style={{ marginRight: 6 }}>
          Show
        </label>
        <select
          id="form-config-type-filter"
          className="form-control input-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">All ticket types ({types.length})</option>
          {types.map((t) => (
            <option key={t.ID} value={String(t.ID)}>
              {t.type_name}
            </option>
          ))}
        </select>
      </div>

      <table className="table form-status-entities">
        <thead>
          <tr>
            <th style={{ width: 100 }}></th>
            <th className="a-left">Ticket type</th>
            <th>Status form configuration</th>
          </tr>
        </thead>
        <tbody>
          {types
            .filter((t) => filter === '' || String(t.ID) === filter)
            .map((type) => (
              <tr key={type.ID} data-type={type.ID}>
                <td className="center">
                  <span className="badge" style={{ minWidth: 23, color: '#FFF', backgroundColor: type.color_identifier }}>
                    {type.short_name}
                  </span>
                </td>
                <td>{type.type_name}</td>
                <td>
                  {type.workflow_id > 0 ? (
                    <table width="100%">
                      <tbody>
                        {type.statuses.map((status) => (
                          <tr key={status.ID}>
                            <td style={{ border: 'none', padding: 4 }}>{status.status_name}</td>
                            <td style={{ border: 'none', padding: 4 }}>
                              <form className="assign_form_status" onSubmit={(e) => e.preventDefault()}>
                                <input type="hidden" name="ticket_type" value={type.ID} />
                                <input type="hidden" name="ticket_status" value={status.ID} />
                                <SelectPicker
                                  id={`form-scheme-${type.ID}-${status.ID}`}
                                  name="form_id"
                                  className="form-control input-sm change-form-scheme"
                                  title="Select form..."
                                  options={formOptions}
                                  value={String(status.form_id)}
                                  onChange={(value) => onAssign(type, status.ID, value)}
                                />
                              </form>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="alert alert-warning" style={{ margin: 0 }}>
                      <i className="fas fa-info-circle" aria-hidden="true"></i> No workflow assigned on this type.
                    </div>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}
